import React, { useState, useEffect } from 'react';
import { Typography, Button, Stack, Box, Menu, MenuItem, IconButton, Drawer, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import BlurCircularOutlinedIcon from '@mui/icons-material/BlurCircularOutlined';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import SportsRugbyOutlinedIcon from '@mui/icons-material/SportsRugbyOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// TopBar styled components from HeroSection
const TopBar = styled('div')<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  backgroundColor: '#455b40',
  padding: '2px 0',
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  width: '100vw',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  transform: `translateY(${isVisible ? '0' : '-100%'})`,
  transition: 'all 0.3s ease-in-out',
  // Responsive padding
  [theme.breakpoints.down('xl')]: {
    padding: '3px 0'
  },
  [theme.breakpoints.down('lg')]: {
    padding: '4px 0'
  },
  [theme.breakpoints.down('md')]: {
    display: 'none'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    padding: '0px 0'
  }
}));

const ContactInfo = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '24px',
  color: '#FFFFFF',
  fontSize: '14px',
  // Responsive gaps and font sizes
  [theme.breakpoints.down('xl')]: {
    gap: '20px',
    fontSize: '13px'
  },
  [theme.breakpoints.down('lg')]: {
    gap: '16px',
    fontSize: '12px'
  },
  [theme.breakpoints.down('md')]: {
    gap: '12px',
    fontSize: '11px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    gap: '28px',
    fontSize: '15px'
  }
}));

const ContactItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px'
}));

const SocialIcons = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '12px'
}));

const SocialIcon = styled(IconButton)(({ theme }) => ({
  color: '#c2b8a8',
  fontSize: '18px',
  padding: '8px',
  minWidth: '44px',
  minHeight: '44px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  },
  '&:active': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)'
  },
  // Responsive sizing
  [theme.breakpoints.down('xl')]: {
    fontSize: '17px',
    minWidth: '42px',
    minHeight: '42px',
    padding: '7px'
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '16px',
    minWidth: '40px',
    minHeight: '40px',
    padding: '6px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '15px',
    minWidth: '38px',
    minHeight: '38px',
    padding: '5px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '20px',
    minWidth: '48px',
    minHeight: '48px',
    padding: '10px'
  }
}));

const MainHeader = styled('div')<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  backgroundColor: isVisible ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: isVisible ? 'blur(10px)' : 'blur(10px)',
  position: 'fixed',
  top: '50px', // Position below TopBar with generous spacing
  left: 0,
  right: 0,
  zIndex: 999,
  padding: '12px 0',
  transform: `translateY(${isVisible ? '0' : '-150px'})`, // Move up enough to hide completely including TopBar
  transition: 'all 0.3s ease-in-out',
  boxShadow: isVisible ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none',
  width: '100vw',
  // Responsive top positioning and transform values
  [theme.breakpoints.down('xl')]: {
    top: '52px',
    transform: `translateY(${isVisible ? '0' : '-155px'})` // Adjust for XL screen TopBar height
  },
  [theme.breakpoints.down('lg')]: {
    top: '54px',
    transform: `translateY(${isVisible ? '0' : '-160px'})` // Adjust for LG screen TopBar height
  },
  [theme.breakpoints.down('md')]: {
    top: '0px',
    transform: `translateY(${isVisible ? '0' : '-100px'})` // TopBar is hidden on mobile
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    top: '58px',
    transform: `translateY(${isVisible ? '0' : '-170px'})` // Adjust for ultra-wide screen TopBar height
  }
}));

const NavButton = styled(Button)<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  color: isVisible ? '#2a3a28' : '#2a3a28',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1.4rem',
  fontFamily: '"Lora", serif',
  padding: '8px 12px',
  minWidth: 'auto',
  borderRadius: '0',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.05)',
    color: '#2a3a28',
    borderRadius: '0'
  },
  // Responsive font sizes
  [theme.breakpoints.down('xl')]: {
    fontSize: '1.3rem',
    padding: '7px 10px'
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '1.2rem',
    padding: '6px 8px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1.1rem',
    padding: '5px 6px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1.6rem',
    padding: '10px 15px'
  },
  // Hide on mobile - will use hamburger menu
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const LetsTalkButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#85b47c',
  color: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.2rem',
  fontFamily: '"Lora", serif',
  padding: '8px 24px',
  borderRadius: '12px',
  border: `2px solid #85b47c`,
  position: 'relative',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  '& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiButton-label': {
    position: 'relative',
    zIndex: 2
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    backgroundColor: '#FFFFFF',
    transition: 'left 0.4s ease',
    zIndex: 1,
    borderRadius: '12px'
  },
  '&::after': {
    content: 'attr(data-hover-text)',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#85b47c',
    fontWeight: 600,
    fontSize: '1.2rem',
    fontFamily: '"Lora", serif',
    zIndex: 3,
    opacity: 0,
    transition: 'opacity 0.4s ease',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  '&:hover': {
    borderColor: '#85b47c',
    backgroundColor: '#85b47c',
    '&::before': {
      left: 0
    },
    '&::after': {
      opacity: 1
    },
    '& span': {
      opacity: 0
    }
  },
  // Responsive sizing
  [theme.breakpoints.down('xl')]: {
    fontSize: '1.1rem',
    padding: '7px 20px'
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '1rem',
    padding: '6px 18px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '0.9rem',
    padding: '5px 16px'
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none' // Hide on mobile
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1.3rem',
    padding: '10px 28px'
  }
}));

const DropdownMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    marginTop: '8px',
    minWidth: '280px',
    // Responsive width
    [theme.breakpoints.down('md')]: {
      minWidth: '240px'
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '200px'
    },
    // Ultra-wide screens
    '@media (min-width: 1920px)': {
      minWidth: '320px'
    }
  },
  '& .MuiList-root': {
    padding: '8px 0'
  }
}));

const DropdownMenuItem = styled(MenuItem)(({ theme }) => ({
  color: '#2a3a28',
  fontSize: '1.2rem',
  fontFamily: '"Lora", serif',
  fontWeight: 500,
  padding: '0',
  minHeight: '48px',
  backgroundColor: 'transparent !important',
  '& a': {
    display: 'block',
    padding: '14px 24px',
    width: '100%',
    textDecoration: 'none',
    color: 'inherit'
  },
  '&:hover': {
    backgroundColor: 'rgba(69, 91, 65, 0.08) !important',
    '& a': {
      color: '#455b41'
    }
  },
  '&.Mui-selected': {
    backgroundColor: 'transparent !important'
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'rgba(69, 91, 65, 0.08) !important'
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'transparent !important'
  },
  // Responsive sizing
  [theme.breakpoints.down('lg')]: {
    fontSize: '1.1rem',
    padding: '12px 20px',
    minHeight: '44px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
    padding: '10px 18px',
    minHeight: '40px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: '8px 16px',
    minHeight: '36px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1.3rem',
    padding: '16px 28px',
    minHeight: '52px'
  }
}));

// Mobile Menu Components
const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  color: '#2a3a28',
  padding: '12px',
  minWidth: '48px',
  minHeight: '48px',
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.05)'
  },
  '&:active': {
    backgroundColor: 'rgba(42, 58, 40, 0.1)'
  },
  [theme.breakpoints.down('sm')]: {
    display: 'flex'
  }
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '280px',
    backgroundColor: '#FFFFFF',
    padding: '20px 0',
    // Responsive drawer width
    [theme.breakpoints.down('sm')]: {
      width: '260px',
      padding: '16px 0'
    },
    '@media (max-width: 480px)': {
      width: '240px',
      padding: '12px 0'
    }
  }
}));

const MobileNavItem = styled(Button)(({ theme }) => ({
  color: '#2a3a28',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1.3rem',
  fontFamily: '"Lora", serif',
  padding: '16px 24px',
  justifyContent: 'space-between',
  width: '100%',
  borderRadius: 0,
  minHeight: '56px',
  touchAction: 'manipulation',
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.05)'
  },
  '&:active': {
    backgroundColor: 'rgba(42, 58, 40, 0.1)'
  },
  // Responsive sizing
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2rem',
    padding: '14px 20px',
    minHeight: '52px'
  },
  '@media (max-width: 480px)': {
    fontSize: '1.1rem',
    padding: '12px 18px',
    minHeight: '48px'
  }
}));

const MobileSubNavItem = styled(Button)(({ theme }) => ({
  color: '#2a3a28',
  textTransform: 'none',
  fontWeight: 400,
  fontSize: '1.1rem',
  fontFamily: '"Lora", serif',
  padding: '12px 40px',
  justifyContent: 'flex-start',
  width: '100%',
  borderRadius: 0,
  minHeight: '48px',
  touchAction: 'manipulation',
  backgroundColor: 'rgba(42, 58, 40, 0.02)',
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.08)'
  },
  '&:active': {
    backgroundColor: 'rgba(42, 58, 40, 0.12)'
  },
  // Responsive sizing
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '10px 36px',
    minHeight: '44px'
  },
  '@media (max-width: 480px)': {
    fontSize: '0.95rem',
    padding: '8px 32px',
    minHeight: '40px'
  }
}));

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true); // Start visible at top
  const [lastScrollY, setLastScrollY] = useState(0);
  const [sobreNosotrosAnchor, setSobreNosotrosAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSobreNosotrosOpen, setMobileSobreNosotrosOpen] = useState(false);
  const [dropdownTimer, setDropdownTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      // Show header when at top or scrolling up, hide when scrolling down
      if (currentScrollY === 0) {
        // At the very top of the page - always show
        setIsVisible(true);
      } else if (currentScrollY > 50) { // Only start hide/show behavior after 50px
        if (currentScrollY < lastScrollY) {
          // Scrolling up - show header
          setIsVisible(true);
        } else {
          // Scrolling down - hide header
          setIsVisible(false);
        }
      }
      // Between 0-50px, maintain current state
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    
    return () => {
      window.removeEventListener('scroll', controlHeader);
    };
  }, [lastScrollY]);

  const handleSobreNosotrosClick = () => {
    navigate('/quienes-somos');
  };

  const handleSobreNosotrosMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    // Clear any existing timer
    if (dropdownTimer) {
      clearTimeout(dropdownTimer);
      setDropdownTimer(null);
    }
    setSobreNosotrosAnchor(event.currentTarget);
  };

  const handleSobreNosotrosMouseLeave = () => {
    // Set a timer to close dropdown
    const timer = setTimeout(() => {
      setSobreNosotrosAnchor(null);
    }, 300);
    setDropdownTimer(timer);
  };

  const handleDropdownMouseEnter = () => {
    // Clear timer when entering dropdown
    if (dropdownTimer) {
      clearTimeout(dropdownTimer);
      setDropdownTimer(null);
    }
  };

  const handleDropdownMouseLeave = () => {
    // Close dropdown when leaving
    setSobreNosotrosAnchor(null);
  };

  const handleClose = () => {
    setSobreNosotrosAnchor(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
    setMobileSobreNosotrosOpen(false);
  };

  const handleMobileSobreNosotrosToggle = () => {
    setMobileSobreNosotrosOpen(!mobileSobreNosotrosOpen);
  };

  return (
    <>
      {/* Top Contact Bar */}
      <TopBar isVisible={isVisible}>
        <div style={{ maxWidth: '1800px', margin: '0 auto', padding: '2px 20px' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <ContactInfo>
              <ContactItem sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <SportsRugbyOutlinedIcon sx={{ fontSize: '16px', color: '#c2b8a8' }} />
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>Finca ecológica</Typography>
              </ContactItem>
              <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(255, 255, 255, 0.3)', margin: '0 8px', display: 'none' }} className="separator" />
              <ContactItem>
                <LocationOnOutlinedIcon sx={{ fontSize: '16px', color: '#c2b8a8' }} />
                <Typography variant="body2" sx={{ color: '#FFFFFF', display: { xs: 'none', md: 'block' } }}>Bétera, Valencia</Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', display: { xs: 'block', md: 'none' } }}>Bétera</Typography>
              </ContactItem>
              <ContactItem sx={{ display: { xs: 'none', lg: 'flex' } }}>
                <MailOutlineOutlinedIcon sx={{ fontSize: '16px', color: '#c2b8a8' }} />
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>info@ekkofood.com</Typography>
              </ContactItem>
              <ContactItem>
                <PhoneOutlinedIcon sx={{ fontSize: '16px', color: '#c2b8a8' }} />
                <Typography variant="body2" sx={{ color: '#FFFFFF', display: { xs: 'none', sm: 'block' } }}>+34 684 238 458</Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', display: { xs: 'block', sm: 'none' } }}>Call</Typography>
              </ContactItem>
            </ContactInfo>
            
            {/* Placeholder to maintain topbar height */}
            <Box sx={{ 
              minHeight: { 
                xs: '38px', 
                md: '38px', 
                lg: '40px', 
                xl: '42px',
                '@media (min-width: 1920px)': '48px'
              } 
            }} />
            
            {/* Language selector and social icons hidden for now */}
            {/* <Stack direction="row" alignItems="center" gap="16px">
              <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(255, 255, 255, 0.3)', margin: '0 8px' }} />
              <Typography variant="body2" sx={{ color: '#FFFFFF' }}>🇺🇸 EN</Typography>
              <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(255, 255, 255, 0.3)', margin: '0 8px' }} />
              <SocialIcons>
                <SocialIcon>
                  <AccountBoxOutlinedIcon sx={{ fontSize: '18px' }} />
                </SocialIcon>
                <SocialIcon>
                  <ClearOutlinedIcon sx={{ fontSize: '18px' }} />
                </SocialIcon>
                <SocialIcon>
                  <BlurCircularOutlinedIcon sx={{ fontSize: '18px' }} />
                </SocialIcon>
                <SocialIcon>
                  <AddAPhotoOutlinedIcon sx={{ fontSize: '18px' }} />
                </SocialIcon>
              </SocialIcons>
            </Stack> */}
          </Stack>
        </div>
      </TopBar>

      <MainHeader isVisible={isVisible}>
        <div style={{ 
          maxWidth: '1800px', 
          margin: '0 auto', 
          padding: '0 20px', 
          width: '100%',
          position: 'relative', // Add relative positioning to container
          // Responsive padding
        }}>
          {/* Logo Image - positioned relative to container */}
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Box
              sx={{
                position: 'absolute',
                width: { xs: '80px', sm: '100px', md: '120px' },
                height: { xs: '75px', sm: '95px', md: '110px' },
                left: {
                  sm: '10px',
                  md: '20px',
                  lg: '35px',
                  xl: '-5px'
                },
                transform: `translateY(${isVisible ? '0' : '-100%'})`,
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 25,
                pointerEvents: 'auto',
                top: '-20px', // Adjusted to account for container padding
                borderRadius: '0px 0px 20px 20px',
                overflow: 'hidden',
                opacity: isVisible ? 1 : 0,
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              <img 
                src="/Kopie von EkkoFood.png" 
                alt="Ekko Food Logo" 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover'
                }}
              />
            </Box>
          </Link>

          <Stack direction="row" alignItems="center" width="100%" justifyContent="space-between">
            {/* EKKOFOOD Text for Mobile */}
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontFamily: '"Lato", serif', 
                  fontWeight: 700, 
                  color: '#2a3a28',
                  fontSize: {
                    xs: '1.3rem',
                    sm: '1.5rem'
                  },
                  minWidth: 'fit-content',
                  display: { xs: 'block', sm: 'none' },
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              >
                EKKOFOOD
              </Typography>
            </Link>

            {/* Mobile Menu Button */}
            <MobileMenuButton onClick={handleMobileMenuToggle} sx={{ marginLeft: 'auto' }}>
              <MenuIcon sx={{ fontSize: '2rem' }} />
            </MobileMenuButton>

            {/* Navigation Menu - Desktop */}
            <Stack 
              direction="row" 
              alignItems="center" 
              gap={{
                xs: '4px',
                sm: '6px', 
                md: '8px',
                lg: '10px'
              }}
              sx={{ 
                position: 'relative', 
                flex: 1, 
                justifyContent: 'flex-start', // Align to left instead of center
                display: { xs: 'none', sm: 'flex' },
                marginLeft: { 
                  sm: '130px', // Enough space to clear the logo
                  md: '140px',
                  lg: '150px'
                },
                '@media (min-width: 1920px)': {
                  marginLeft: '160px' // More space on ultra-wide screens
                }
              }}
            >
              <Link to="/" style={{ textDecoration: 'none' }}>
                <NavButton isVisible={isVisible}>Inicio</NavButton>
              </Link>
              <NavButton 
                isVisible={isVisible}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '1.7rem' }} />} 
                onClick={handleSobreNosotrosClick}
                onMouseEnter={handleSobreNosotrosMouseEnter}
                onMouseLeave={handleSobreNosotrosMouseLeave}
                aria-controls={sobreNosotrosAnchor ? 'sobre-nosotros-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={sobreNosotrosAnchor ? 'true' : undefined}
              >
                Quiénes Somos
              </NavButton>
              
              <Link to="/cursos" style={{ textDecoration: 'none' }}>
                <NavButton isVisible={isVisible}>Cursos</NavButton>
              </Link>
              <Link to="/noticias-blog" style={{ textDecoration: 'none' }}>
                <NavButton isVisible={isVisible}>Noticias & Blog</NavButton>
              </Link>
              <Link to="/contacto" style={{ textDecoration: 'none' }}>
                <NavButton isVisible={isVisible}>Contacto</NavButton>
              </Link>
            </Stack>
            
            {/* Let's Talk Button - moved to right edge */}
            <Link to="/cursos" style={{ textDecoration: 'none' }}>
              <LetsTalkButton sx={{ minWidth: 'fit-content' }} data-hover-text="Próximos Cursos →">
              Próximos Cursos →
              </LetsTalkButton>
            </Link>
          </Stack>
        </div>
      </MainHeader>

      {/* Sobre Nosotros Dropdown */}
      <DropdownMenu
        id="sobre-nosotros-menu"
        anchorEl={sobreNosotrosAnchor}
        open={Boolean(sobreNosotrosAnchor)}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'sobre-nosotros-button',
          onMouseEnter: handleDropdownMouseEnter,
          onMouseLeave: handleDropdownMouseLeave
        }}
        sx={{
          pointerEvents: 'none',
          '& .MuiPaper-root': {
            pointerEvents: 'auto',
            mt: 0.5
          }
        }}
      >
        <DropdownMenuItem onClick={handleClose}>
          <Link to="/mission-vision">
            Misión & Visión
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleClose}>
          <Link to="/la-finca">
            La Finca
          </Link>
        </DropdownMenuItem>
      </DropdownMenu>

      {/* Mobile Navigation Drawer */}
      <MobileDrawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '16px 20px',
          borderBottom: '1px solid rgba(42, 58, 40, 0.1)'
        }}>
          <Link to="/" style={{ textDecoration: 'none' }} onClick={handleMobileMenuClose}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: '"Lato", serif', 
                fontWeight: 700, 
                color: '#2a3a28',
                fontSize: '1.2rem',
                cursor: 'pointer'
              }}
            >
              EKKOFOOD
            </Typography>
          </Link>
          <IconButton 
            onClick={handleMobileMenuClose}
            sx={{ 
              color: '#2a3a28',
              padding: '8px'
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ padding: '8px 0' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <MobileNavItem onClick={handleMobileMenuClose}>
              <span>Inicio</span>
            </MobileNavItem>
          </Link>
          
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Link to="/quienes-somos" style={{ textDecoration: 'none', flex: 1 }}>
              <MobileNavItem 
                onClick={handleMobileMenuClose}
                sx={{ 
                  borderRadius: 0,
                  justifyContent: 'flex-start',
                  '&:hover': {
                    backgroundColor: 'rgba(42, 58, 40, 0.05)'
                  }
                }}
              >
                <span>Quiénes Somos</span>
              </MobileNavItem>
            </Link>
            <IconButton
              onClick={handleMobileSobreNosotrosToggle}
              sx={{
                color: '#2a3a28',
                padding: '12px 16px',
                borderLeft: '1px solid rgba(42, 58, 40, 0.1)',
                borderRadius: 0,
                minWidth: '56px',
                '&:hover': {
                  backgroundColor: 'rgba(42, 58, 40, 0.05)'
                }
              }}
            >
              {mobileSobreNosotrosOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={mobileSobreNosotrosOpen} timeout="auto" unmountOnExit>
            <Link to="/mission-vision" style={{ textDecoration: 'none' }}>
              <MobileSubNavItem onClick={handleMobileMenuClose}>
                Misión & Visión
              </MobileSubNavItem>
            </Link>
            <Link to="/la-finca" style={{ textDecoration: 'none' }}>
              <MobileSubNavItem onClick={handleMobileMenuClose}>
                La Finca
              </MobileSubNavItem>
            </Link>
          </Collapse>
          
          <Link to="/cursos" style={{ textDecoration: 'none' }}>
            <MobileNavItem onClick={handleMobileMenuClose}>
              <span>Cursos</span>
            </MobileNavItem>
          </Link>
          
          <Link to="/noticias-blog" style={{ textDecoration: 'none' }}>
            <MobileNavItem onClick={handleMobileMenuClose}>
              <span>Noticias & Blog</span>
            </MobileNavItem>
          </Link>
          
          <Link to="/contacto" style={{ textDecoration: 'none' }}>
            <MobileNavItem onClick={handleMobileMenuClose}>
              <span>Contacto</span>
            </MobileNavItem>
          </Link>
        </Box>
        
        <Box sx={{ 
          padding: '20px',
          borderTop: '1px solid rgba(42, 58, 40, 0.1)',
          marginTop: 'auto'
        }}>
          <Link to="/cursos" style={{ textDecoration: 'none' }}>
            <Button
              fullWidth
              onClick={handleMobileMenuClose}
              sx={{
                backgroundColor: '#85b47c',
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1.1rem',
                fontFamily: '"Lora", serif',
                padding: '12px 24px',
                borderRadius: '12px',
                '&:hover': {
                  backgroundColor: '#6a8f61'
                }
              }}
            >
              Próximos Cursos →
            </Button>
          </Link>
        </Box>
      </MobileDrawer>
    </>
  );
};

export default Header;